import React from "react";

// CSS/SVG abstract dashboard mockup — clean, Linear/Stripe-inspired
const HeroGraphic = () => {
  return (
    <div className="relative w-full aspect-[5/6] md:aspect-square max-w-[560px] mx-auto" data-testid="hero-graphic">
      {/* Glow background */}
      <div className="absolute -inset-12 hero-glow pointer-events-none" />

      {/* Floating mini stats card top-right */}
      <div className="absolute -top-2 right-2 md:right-0 z-20 bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_10px_40px_rgba(17,17,17,0.08)] p-4 w-[180px] float-up reveal-delay-2">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot" />
          <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">Performance</span>
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-display text-3xl font-semibold text-[#111111]">+38%</span>
          <span className="text-xs text-[#10B981] font-medium">vs Q1</span>
        </div>
        <div className="mt-3 flex items-end gap-1 h-8">
          {[40, 65, 50, 80, 55, 90, 75].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm bg-gradient-to-t from-[#2563EB] to-[#60A5FA] bar-animate"
              style={{ height: `${h}%`, animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>

      {/* Main dashboard card */}
      <div className="absolute inset-0 top-12 md:top-16 rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_30px_80px_rgba(17,17,17,0.08)] overflow-hidden float-up">
        {/* Top window controls */}
        <div className="flex items-center gap-1.5 px-4 h-9 border-b border-[#E5E7EB] bg-[#FAFBFC]">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          <div className="ml-4 px-3 py-0.5 rounded-md bg-white border border-[#E5E7EB] text-[10px] text-[#6B7280] font-mono">
            zuindigital.it/dashboard
          </div>
        </div>

        {/* Content */}
        <div className="p-5 md:p-6 space-y-4">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Overview</div>
              <div className="font-display text-lg font-semibold text-[#111111]">Q2 Insights</div>
            </div>
            <div className="flex items-center gap-1">
              <div className="px-2 py-1 rounded-md bg-[#F5F7FA] text-[10px] font-medium text-[#111111]">7d</div>
              <div className="px-2 py-1 rounded-md bg-[#111111] text-[10px] font-medium text-white">30d</div>
              <div className="px-2 py-1 rounded-md bg-[#F5F7FA] text-[10px] font-medium text-[#111111]">90d</div>
            </div>
          </div>

          {/* Line chart */}
          <div className="relative h-32 rounded-xl bg-[#F5F7FA] p-3 overflow-hidden">
            <svg viewBox="0 0 240 100" className="w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0 70 L30 60 L60 65 L90 45 L120 50 L150 30 L180 35 L210 18 L240 22 L240 100 L0 100 Z"
                fill="url(#grad1)"
              />
              <path
                d="M0 70 L30 60 L60 65 L90 45 L120 50 L150 30 L180 35 L210 18 L240 22"
                fill="none"
                stroke="#2563EB"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="draw-line"
              />
              <circle cx="210" cy="18" r="3.5" fill="#2563EB" />
              <circle cx="210" cy="18" r="6" fill="#2563EB" opacity="0.2" className="pulse-dot" />
            </svg>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { l: "Clienti", v: "127" },
              { l: "Progetti", v: "42" },
              { l: "ROI", v: "+24%" },
            ].map((s, i) => (
              <div key={i} className="rounded-lg border border-[#E5E7EB] p-2.5">
                <div className="text-[9px] uppercase tracking-wider text-[#6B7280]">{s.l}</div>
                <div className="font-display text-base font-semibold text-[#111111]">{s.v}</div>
              </div>
            ))}
          </div>

          {/* Task list */}
          <div className="space-y-1.5">
            {[
              { t: "Automazione CRM", d: "Completato", c: "#10B981" },
              { t: "Cloud migration", d: "In corso · 78%", c: "#2563EB" },
            ].map((it, i) => (
              <div key={i} className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-[#F5F7FA]">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: it.c }} />
                  <span className="text-xs font-medium text-[#111111]">{it.t}</span>
                </div>
                <span className="text-[10px] text-[#6B7280]">{it.d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating bottom-left badge */}
      <div className="absolute -bottom-2 left-0 md:-left-4 z-20 bg-[#111111] text-white rounded-2xl px-4 py-3 shadow-2xl float-up reveal-delay-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#2563EB] flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/60">Sistema</div>
            <div className="text-xs font-medium">Operativo · 99.9%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroGraphic;
